import { useEffect, useMemo, useRef } from 'react';
import Image from 'next/image';
import styles from './FeaturedBox.module.scss';
import { FeaturedBoxSlice } from '@adapters/prismic/types/brandedStorePage';
import Button from '@components/ui/Button';
import MarkdownCopy from '@components/cms/Slices/MarkdownCopy';
import Container from '@components/global/Container';
import cn from 'classnames';
import {
  trackPromotionEvent,
  PromotionEventName,
  InteractionLocation,
  FeaturedBoxEventProps,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';

import useObjectVisibility from '@hooks/useObjectVisibility';

const FeaturedBox = ({
  primary,
  locale = 'en',
  brandedStoreCoopAttribute,
  sliceIndex,
}: FeaturedBoxSlice) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useObjectVisibility(ref);

  const promoDetails = useMemo(
    () => ({
      page: {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        page_type: determinePageType(),
      },
      interaction: {
        coop_brand: brandedStoreCoopAttribute || null,
        interaction_location: InteractionLocation.FeaturedBox,
      },
      ecommerce: {
        creative_name: `link,${primary.cta_url}`,
        creative_slot: buildCreativeSlotName({
          sliceIndex,
          interactionLocation: InteractionLocation.FeaturedBox,
        }),
        promotion_id: primary.title || null,
        promotion_name: primary.cta_label || null,
        isVideo: false,
      },
    }),
    [
      brandedStoreCoopAttribute,
      primary.cta_label,
      primary.cta_url,
      primary.title,
      sliceIndex,
    ]
  );

  useEffect(() => {
    if (isVisible) {
      trackPromotionEvent({
        eventName: PromotionEventName.viewPromotion,
        ...promoDetails,
      } as FeaturedBoxEventProps);
    }
  }, [isVisible, primary, promoDetails]);

  const handleCTAClick = () => {
    trackPromotionEvent({
      eventName: PromotionEventName.selectPromotion,
      ...promoDetails,
    } as FeaturedBoxEventProps);
  };

  return (
    <Container className={styles.fullBleed}>
      <div id={primary.id} className={styles.container} ref={ref}>
        <div className={styles.content}>
          <section
            className={cn(styles.textContent, {
              [styles.noOverlay]: !primary.overlay_image.url,
            })}
          >
            {primary.overlay_image.url && (
              <div className={styles.imageContainerMobile}>
                <Image
                  src={primary.overlay_image.url}
                  alt={primary.overlay_image.alt || ''}
                  fill
                  sizes="100vw"
                  style={{
                    objectFit: 'contain',
                  }}
                />
              </div>
            )}
            <div className={styles.titleContainer}>
              <h1>{primary.title}</h1>
            </div>
            {primary?.copy?.[0]?.text && (
              <MarkdownCopy
                text={primary?.copy?.[0]?.text ?? ''}
                className={styles.copy}
              />
            )}
            {primary?.cta_label && primary?.cta_url && (
              <div className={styles.ctaContainer}>
                <Button
                  className={styles.cta}
                  label={primary.cta_label}
                  size="large"
                  type="button"
                  variant="secondary"
                  href={`/${locale}${primary.cta_url}`}
                  handleClick={handleCTAClick}
                />
              </div>
            )}
          </section>
          {primary.overlay_image.url && (
            <div className={styles.imageContainer}>
              <Image
                src={primary.overlay_image.url}
                alt={primary.overlay_image.alt || ''}
                fill
                sizes="100vw"
                style={{
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default FeaturedBox;
